import * as Phaser from "phaser-ce"
import {
    activateApp,
    AppRoutes,
    browserHistory,
} from "../../../common/AppsRouter"
import { AppType } from "../../../common/AppType"
import { CCUtils } from "../../../common/CCUtils"
import { COLOR } from "../../../common/Color"
import { log } from "../../../common/Logging"
import { ActivityProgressStoreSA } from "../../../react/activity-progress/ActivityProgressStoreSA"
import { ActivityKey } from "../../../shared/models/ActivityKey"
import { GameState, TransitionState } from "../../GameState"
import { Buttons } from "../../generic-sprites/Buttons"
import { DebugPanel } from "../../generic-sprites/DebugPanel"
import { loadGenericAssets } from "../../generic-sprites/GenericAssets"
import { ColorUtils } from "../../utils/ColorUtils"
import { PhaserUtils } from "../../utils/PhaserUtils"
import { Scale } from "../../utils/Scale"
import { Videos } from "../../utils/Videos"

export interface TransitionInfo {
    activityLocation: string
    activity: ActivityKey
    outroVideoButtonExit: boolean
    hasTestimonials: boolean
}

export class Transitions {
    static intro = (game: Phaser.Game, activity: ActivityKey): GameState => {
        return (activity + "-intro") as TransitionState
    }
    static outro = (game: Phaser.Game, activity: ActivityKey) => {
        return (activity + "-outro") as TransitionState
    }
    static startOutro = (game: Phaser.Game, activity: ActivityKey) => {
        game.state.start(Transitions.outro(game, activity))
    }

    static registerTransitions = (
        game: Phaser.Game,
        infos: Array<TransitionInfo>,
    ) => {
        infos.forEach((info) => {
            // Everything gets an intro buddy state
            game.state.add(
                info.activity + "-intro",
                new Transition(info, "intro"),
            )

            // Outros for buddy activities that do not have testimonials. If they do, it is handled by a custom State.
            if (info.hasTestimonials) {
                game.state.add(
                    info.activity + "-outro2",
                    new Transition(info, "outro2"),
                )
            } else {
                game.state.add(
                    info.activity + "-outro",
                    new Transition(info, "outro"),
                )
            }
        })
    }
}

class Transition extends Phaser.State {
    constructor(
        private info: TransitionInfo,
        private type: "intro" | "outro" | "outro2",
    ) {
        super()
    }

    preload() {
        loadGenericAssets(this.game)
        const videoLocation = CCUtils.asset(
            CCUtils.genderifyFileName(
                `${this.info.activityLocation}/transitions/${this.type}.mp4`,
            ),
        )
        log.debug(`Video location: ${videoLocation}`)
        this.game.load.video(this.videoKey(), videoLocation)
    }

    async create() {
        await PhaserUtils.sharedCreateFunctions(this.game)
        Scale.resizeScreen(this.game, true)
        ColorUtils.setBackgroundColor(this.game, COLOR.black)

        const onNextClicked = () => {
            switch (this.type) {
                case "intro":
                    if (this.info.activityLocation === "maze") {
                        activateApp(AppType.BABYLON)
                    } else {
                        this.game.state.start(this.info.activity)
                    }
                    break
                case "outro":
                case "outro2":
                    ActivityProgressStoreSA.instance
                        .updateActivityProgress(this.info.activity)
                        .then(() => {
                            if (this.info.outroVideoButtonExit) {
                                browserHistory.push(AppRoutes.home)
                            } else {
                                this.game.state.start(TransitionState.MAP)
                            }
                        })
                        .catch((error) => {
                            throw new Error(`Firestore update failed: ${error}`)
                        })
                    break
                default:
                    throw new Error("Unrecognized transition type.")
            }
        }

        const video = await Videos.add(this.game, this.videoKey(), {
            onComplete: () => {
                const buttonText =
                    (this.type === "outro" || this.type === "outro2") &&
                    this.info.outroVideoButtonExit
                        ? "Exit"
                        : "Next"
                Buttons.create(this.game, buttonText, onNextClicked, {
                    x: Scale.x(0.874),
                    y: Scale.y(0.94),
                })
            },
            onException: () => {
                onNextClicked()
            },
            fillScreen: true,
        })
        const debugPanel = new DebugPanel(this.game)
        debugPanel.setSkip(() => {
            video.destroy()
            onNextClicked()
        })
        ActivityProgressStoreSA.instance.updateActivityProgress(
            this.info.activity,
            true,
        )
    }

    videoKey = () => this.info.activity + this.type
}
